import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { FormDialog, Field, TextInput, SelectInput, ActionButton } from "./common";
import { JOB_STATUSES } from "./JobTicketForm";

const newSelection = () => ({ customer_id: "", date_from: "", date_to: "", job_statuses: [], _k: Math.random().toString(36).slice(2, 10) });

export const BatchOrderDialog = ({ open, onOpenChange, onSaved }) => {
  const [customers, setCustomers] = useState([]);
  const [selections, setSelections] = useState([newSelection()]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      api.get("/customers").then((r) => setCustomers(r.data)).catch(() => {});
      setSelections([newSelection()]);
    }
  }, [open]);

  const setSel = (i, patch) => setSelections((prev) => prev.map((s, idx) => (idx === i ? { ...s, ...patch } : s)));

  const toggleStatus = (i, status) =>
    setSel(i, {
      job_statuses: selections[i].job_statuses.includes(status)
        ? selections[i].job_statuses.filter((s) => s !== status)
        : [...selections[i].job_statuses, status],
    });

  const customerOptions = [...customers].sort((a, b) => a.name.localeCompare(b.name)).map((c) => ({ value: c.id, label: c.name }));

  const submit = async () => {
    const valid = selections.filter((s) => s.customer_id);
    if (valid.length === 0) {
      toast.error("Select a customer in at least one batch selection");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        selections: valid.map((s) => ({
          customer_id: s.customer_id,
          ...(s.date_from ? { date_from: s.date_from } : {}),
          ...(s.date_to ? { date_to: s.date_to } : {}),
          job_statuses: s.job_statuses,
        })),
      };
      const r = await api.post("/swd/sales-orders/batch", payload);
      toast.success(
        `Batch order ${r.data.order_number} created — ${r.data.ticket_ids.length} job ticket(s), ${r.data.barrels} total qty`
      );
      onOpenChange(false);
      onSaved?.();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <FormDialog open={open} onOpenChange={onOpenChange} title="New Batch Sales Order" testId="batch-dialog">
      <div className="space-y-5">
        {selections.map((sel, i) => (
          <div key={sel._k} className="border border-slate-700 rounded-md p-4 space-y-4 relative" data-testid={`batch-selection-${i}`}>
            {selections.length > 1 && (
              <button
                type="button"
                data-testid={`batch-remove-${i}`}
                onClick={() => setSelections(selections.filter((_, idx) => idx !== i))}
                className="absolute top-3 right-3 p-1 rounded text-slate-500 hover:text-red-400 hover:bg-red-500/10 transition-colors duration-200"
              >
                <Trash2 size={13} />
              </button>
            )}
            <p className="text-[10px] font-mono uppercase tracking-[0.25em] text-amber-500/80">Batch selection {i + 1}</p>
            <Field label="Customer">
              <SelectInput
                testId={`batch-customer-${i}`}
                value={sel.customer_id}
                onChange={(e) => setSel(i, { customer_id: e.target.value })}
                placeholder="Select customer"
                options={customerOptions}
              />
            </Field>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Date from">
                <TextInput testId={`batch-date-from-${i}`} type="date" value={sel.date_from} onChange={(e) => setSel(i, { date_from: e.target.value })} />
              </Field>
              <Field label="Date to">
                <TextInput testId={`batch-date-to-${i}`} type="date" value={sel.date_to} onChange={(e) => setSel(i, { date_to: e.target.value })} />
              </Field>
            </div>
            <div>
              <span className="block text-xs font-mono uppercase tracking-[0.15em] text-slate-400 mb-1.5">Job Status (none selected = all)</span>
              <div className="flex flex-wrap gap-2" data-testid={`batch-statuses-${i}`}>
                {JOB_STATUSES.map((s) => (
                  <button
                    key={s}
                    type="button"
                    data-testid={`batch-status-${i}-${s.toLowerCase().replace(/\s+/g, "-")}`}
                    onClick={() => toggleStatus(i, s)}
                    className={`px-2.5 py-1 rounded-md border text-xs transition-colors duration-200 ${
                      sel.job_statuses.includes(s)
                        ? "bg-amber-500/10 text-amber-400 border-amber-500/40"
                        : "text-slate-400 border-slate-700 hover:border-slate-500"
                    }`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          </div>
        ))}

        <div className="flex items-start gap-4">
          <ActionButton
            testId="batch-add-another-button"
            variant="secondary"
            onClick={() => setSelections([...selections, newSelection()])}
          >
            <Plus size={16} /> Add another batch selection
          </ActionButton>
          <ActionButton testId="batch-complete-button" onClick={submit} disabled={busy} className="flex-1 justify-center">
            {busy ? "Building…" : "Complete This Sales Order"}
          </ActionButton>
        </div>
        <p className="text-[11px] text-slate-500 -mt-2" data-testid="batch-add-another-note">
          “Add another batch selection” adds more criteria for this sales order only.
        </p>
        <p className="text-[11px] text-amber-500/80 font-mono uppercase tracking-wider" data-testid="batch-approval-note">
          Note: This sales order will be sent for approval, not to be invoiced at this point.
        </p>
      </div>
    </FormDialog>
  );
};
